import React from 'react';
import type { TemplateConfig } from '../../types';

interface TemplateCardProps {
  template: TemplateConfig;
  isSelected: boolean;
  onSelect: () => void;
}

export function TemplateCard({ template, isSelected, onSelect }: TemplateCardProps) {
  const { name, description, duration, resolution, aspectRatio, thumbnail } =
    template.metadata;

  const isVertical = aspectRatio === '9:16';

  return (
    <button
      type="button"
      onClick={onSelect}
      className={[
        'group w-full text-left rounded-lg border-2 p-3 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
        isSelected
          ? 'border-blue-500 bg-blue-950/40'
          : 'border-zinc-700 bg-zinc-800 hover:border-zinc-500 hover:bg-zinc-700/60',
      ].join(' ')}
      aria-pressed={isSelected}
      aria-label={`Select template: ${name}`}
    >
      {/* Thumbnail */}
      <div
        className={[
          'relative w-full overflow-hidden rounded-md bg-zinc-900 mb-3',
          isVertical ? 'aspect-[9/16] max-h-40' : 'aspect-video',
        ].join(' ')}
      >
        {thumbnail ? (
          <img
            src={thumbnail}
            alt={`${name} preview`}
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : null}

        {/* Fallback placeholder */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span
            className="text-xs font-mono text-zinc-500 select-none"
            aria-hidden="true"
          >
            {resolution}
          </span>
        </div>

        {/* Selected badge */}
        {isSelected && (
          <div className="absolute top-2 right-2 bg-blue-500 rounded-full p-1">
            <svg
              className="w-3 h-3 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={3}
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )}
      </div>

      {/* Info */}
      <div>
        <p
          className={[
            'text-sm font-semibold leading-snug mb-0.5',
            isSelected ? 'text-blue-300' : 'text-zinc-100 group-hover:text-white',
          ].join(' ')}
        >
          {name}
        </p>
        <p className="text-xs text-zinc-400 leading-snug line-clamp-2">{description}</p>

        {/* Meta chips */}
        <div className="flex gap-1.5 mt-2 flex-wrap">
          <Chip>{resolution}</Chip>
          <Chip>{duration}s</Chip>
          <Chip>{aspectRatio}</Chip>
        </div>
      </div>
    </button>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-mono bg-zinc-700 text-zinc-300">
      {children}
    </span>
  );
}
